import React from 'react';
import { withStyles, Paper } from '@material-ui/core';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import History from '../../../History';
import * as actions from './store/externalSystemSettingActions';
import { BackButton, parseURL, isValidRegex, isValidMapping } from '../../sc-utilities';
import FormStep1Service from './FormSteps/FormStep1Service';
import FormStep1ServiceNow from './FormSteps/FormStep1ServiceNow';
import FormStep1Database from './FormSteps/FormStep1Database';
import FormStep1Json from './FormSteps/FormStep1Json';
import FormStep1Kloudless from './FormSteps/FormStep1Kloudless';
import FormStep1Makor from './FormSteps/FormStep1Makor';
import FormStep1StoreExternalData from './FormSteps/FormStep1StoreExternalData';


const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    }
});

const ExternalSystemSettingCreate = props => {
    const { activeCustomerID, translations, classes, onCreateExternalSystemSetting } = props;
    let searchParams = parseURL(window.location.search);

    const submitHandler = async data => {
        let result = await onCreateExternalSystemSetting({ ...data, ActiveCustomerID: activeCustomerID });
        if (result) {
            History.goBack();
        }
    }

    let initialValues = {
        Type: searchParams.type,
        Name: '',
        Direction: 'in',
        Active: true,
        ServiceType: '',
        Hostname: '',
        Protocol: 'http:',
        AuthType: 'bearer',
        Port: '',
        UseExternalTypicalProblems: false,
        DatabaseName: '',
        TableName: '',
        Username: '',
        Password: '',
        BearerToken: '',
        ApiKey: '',
        ClientID: '',
        ClientSecret: '',
        ClientIDHeaderName: '',
        ClientIDHeaderValue: '',
        ClientSecretHeaderName: '',
        ClientSecretHeaderValue: '',
        CustomMapping: false,
        TableMapping: '{"PCProduct": {}, "MobileProduct": {}, "StorageProduct": {}, "DisplayProduct": {}}',
        Path: '',
        TriggerOnSessionStart: true,
        TriggerOnModifiedWeb: true,
        TriggerOnModifiedProcessing: true,
        TriggerOnErasureProgress: false,
        TriggerOnErasureFinished: true,
        TriggerOnSessionEnd: true,
        TriggerOnAnalyzeFinished: false,
        TriggerOnOperatorWorkflowDone: false,
        TriggerOnReportGenerated: false,
        SendReportMethod: 'base64pdf',
        SendReportOnSuccessfulErasure: false,
        SendReportOnFailedErasure: false,
        SendReportOnNoErasure: false,
        OutputFormat: 'json',
        ProvideExternalIDEnabled: {
            PCProduct: false,
            MobileProduct: false,
            StorageProduct: false
        },
        ProvideExternalIDRegexp: '',
        ProvideExternalIDDescription: '',
        ProvideExternalIDName: '',
        AutostartErasureOnMethodEnabled: false,
        ConfirmDataExportEnabled: false,
        ConfirmDataExportMessage: '',
        SendErasureReportEndpoint: '',
        SendPerformedDiagnoseCasesOnly: true,
        ErasureMethodMapping: '{}'
    };

    let formSettings = {
        Validation: 
        {
            Type: Yup.string().required('Required'),
            Name: Yup.string().required(),
            TriggerOnSessionStart: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnModifiedWeb: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnModifiedProcessing: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnSessionEnd: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnErasureFinished: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnAnalyzeFinished: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            TriggerOnOperatorWorkflowDone: Yup.boolean().when('Type', {
                is: val => val !== 'store_external_data',
                then: Yup.boolean().required()
            }),
            Hostname: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'service' || val === 'json' || val === 'makor',
                then: Yup.string().required()
            }),
            Port: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'service' || val === 'json',
                then: Yup.string().required()
            }),
            DatabaseName: Yup.string().when('Type', {
                is: val => val === 'database',
                then: Yup.string().required()
            }),
            TableName: Yup.string().when('Type', {
                is: val => val === 'database',
                then: Yup.string().required()
            }),
            InstanceID: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            ClientID: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            ClientSecret: Yup.string().when('Type', {
                is: val => val === 'servicenow',
                then: Yup.string().required()
            }),
            BearerToken: Yup.string().when('Type', {
                is: val => val === 'kloudless',
                then: Yup.string().required()
            }),
            Path: Yup.string().when('Type', {
                is: val => val === 'json' || val === 'makor',
                then: Yup.string().required()
            }),
            CustomMapping: Yup.boolean().when('Type', {
                is: val => val === 'makor',
                then: Yup.boolean().required()
            }),
            TableMapping: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'json' || val === 'kloudless' || val === 'servicenow',
                then: Yup.string().required().test('jsonTest', 'Not a valid JSON', value => isValidMapping(value)),
            }),
            ProvideExternalIDRegexp: Yup.string().when('Type', {
                is: val => val === 'json',
                then: Yup.string().when(['ProvideExternalIDEnabled.PCProduct', 'ProvideExternalIDEnabled.MobileProduct', 'ProvideExternalIDEnabled.StorageProduct'], {
                    is: (pcProduct, mobileProduct, storageProduct) => pcProduct || mobileProduct || storageProduct,
                    then: Yup.string().test('regexTest', 'Not a valid Regular Expression', value => isValidRegex(value))
                })
            }),
            Username: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'servicenow',
                then: Yup.string().required("Required")
            }),
            Password: Yup.string().when('Type', {
                is: val => val === 'database' || val === 'servicenow',
                then: Yup.string().required("Required")
            }),
        },
        ButtonSubmit: {
            Title: translations['save'] || 'SAVE',
            Color: 'primary'
        },
    };

    const formProps={
        handleSubmit: submitHandler, 
        initialValues, 
        translations, 
        formSettings,
    }

    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            {initialValues.Type === 'service' ?
                <FormStep1Service {...formProps}/>
            :
            initialValues.Type === 'database' ? 
                <FormStep1Database {...formProps}/>
            :
            initialValues.Type === 'json' ? 
                <FormStep1Json {...formProps}/>
            :
            initialValues.Type === 'kloudless' ? 
                <FormStep1Kloudless {...formProps}/>
            :
            initialValues.Type === 'servicenow' ? 
                <FormStep1ServiceNow {...formProps}/>
            :
            initialValues.Type === 'makor' ? 
                <FormStep1Makor {...formProps}/>
            : 
            initialValues.Type === 'store_external_data' ? 
                <FormStep1StoreExternalData {...formProps}/>
        :null}
        </Paper >
    )
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onCreateExternalSystemSetting: setting => dispatch(actions.createExternalSystemSetting(setting)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ExternalSystemSettingCreate));